import React, { useState, useMemo, useEffect } from "react";
import FlightCard from "./FlightCard";
import flights from "../data/flights";

const SORT_OPTIONS = [
  { value: "price_asc", label: "Price: Low to High" },
  { value: "price_desc", label: "Price: High to Low" },
  { value: "duration", label: "Duration" },
  { value: "rating", label: "Best Rated" },
];

const AIRLINES = ["All", ...new Set(flights.map((f) => f.airline))];

function FlightList({ onSelectFlight, selectedFlight }) {
  const [searchFrom, setSearchFrom] = useState("");
  const [searchTo, setSearchTo] = useState("");
  const [sortBy, setSortBy] = useState("price_asc");
  const [filterAirline, setFilterAirline] = useState("All");
  const [maxPrice, setMaxPrice] = useState(10000);
  const [isLoading] = useState(false);

  // --- NEW: 3 & 6. State for Compare and Recent Searches ---
  const [compareList, setCompareList] = useState([]);
  const [recentSearch, setRecentSearch] = useState(localStorage.getItem("recentSearch") || "");

  // Helper for time calculation
  const toMins = (d) => {
    const [h, m] = d.replace("h ", ":").replace("m", "").split(":");
    return parseInt(h) * 60 + (parseInt(m) || 0);
  };

  // --- NEW: 6. Store Recent Search logic ---
  useEffect(() => {
    if (searchFrom && searchTo) {
      const term = `${searchFrom.toUpperCase()} → ${searchTo.toUpperCase()}`;
      setRecentSearch(term);
      localStorage.setItem("recentSearch", term);
    }
  }, [searchFrom, searchTo]);

  const filtered = useMemo(() => {
    let result = flights.filter((f) => {
      const matchFrom = f.from.toLowerCase().includes(searchFrom.toLowerCase()) ||
        f.fromCode.toLowerCase().includes(searchFrom.toLowerCase());
      const matchTo = f.to.toLowerCase().includes(searchTo.toLowerCase()) ||
        f.toCode.toLowerCase().includes(searchTo.toLowerCase());
      const matchAirline = filterAirline === "All" || f.airline === filterAirline;
      const matchPrice = f.price <= maxPrice;
      return matchFrom && matchTo && matchAirline && matchPrice;
    });

    result = [...result].sort((a, b) => {
      if (sortBy === "price_asc") return a.price - b.price;
      if (sortBy === "price_desc") return b.price - a.price;
      if (sortBy === "rating") return b.rating - a.rating;
      if (sortBy === "duration") return toMins(a.duration) - toMins(b.duration);
      return 0;
    });

    return result;
  }, [searchFrom, searchTo, sortBy, filterAirline, maxPrice]);

  // --- NEW: 1. Recommended Logic (Price + Rating + Duration) ---
  const recommendedId = useMemo(() => {
    if (flights.length === 0) return null;
    return flights.reduce((prev, curr) => {
      const score = (f) => (f.rating * 20) - (f.price / 500) - (toMins(f.duration) / 10);
      return score(curr) > score(prev) ? curr : prev;
    }).id;
  }, []);

  // --- NEW: 5. Enhanced Summary Panel (Smart Summary) ---
  const cheapestFlight = useMemo(() => flights.reduce((a, b) => a.price < b.price ? a : b), []);
  const fastestFlight = useMemo(() => flights.reduce((a, b) => toMins(a.duration) < toMins(b.duration) ? a : b), []);
  const bestRatedFlight = useMemo(() => flights.reduce((a, b) => b.rating > a.rating ? b : a), []);

  // --- NEW: 3. Compare Toggle ---
  const handleCompare = (flight) => {
    if (compareList.find(f => f.id === flight.id)) {
      setCompareList(compareList.filter(f => f.id !== flight.id));
    } else if (compareList.length < 2) {
      setCompareList([...compareList, flight]);
    }
  };

// 🔥 NEW: Find better flight (only when 2 selected)
const betterFlight =
  compareList.length === 2
    ? (compareList[0].price < compareList[1].price
        ? compareList[0].id
        : compareList[1].id)
    : null;

  return (
    <section className="flight-list-section">
      {/* --- NEW: 6. Recent Search Indicator --- */}
      {recentSearch && (
        <div className="recent-search-pill">
          <span className="pill-label">Recent Search:</span> {recentSearch}
        </div>
      )}

      {/* Search Bar (Original Unchanged) */}
      <div className="search-bar">
        <div className="search-field">
          <span className="search-field-icon">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </span>
          <input className="search-input" placeholder="From city or code" value={searchFrom} onChange={(e) => setSearchFrom(e.target.value)} />
        </div>
        <div className="search-swap">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4"/>
          </svg>
        </div>
        <div className="search-field">
          <span className="search-field-icon">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
            </svg>
          </span>
          <input className="search-input" placeholder="To city or code" value={searchTo} onChange={(e) => setSearchTo(e.target.value)} />
        </div>
      </div>

      {/* Filters Row (Original Unchanged) */}
      <div className="filters-row">
        <div className="filter-group">
          <label className="filter-label">Airline</label>
          <div className="filter-chips">
            {AIRLINES.map((a) => (
              <button key={a} className={`chip ${filterAirline === a ? "chip--active" : ""}`} onClick={() => setFilterAirline(a)}>{a}</button>
            ))}
          </div>
        </div>
        <div className="filter-group filter-group--right">
          <label className="filter-label">Sort by</label>
          <select className="sort-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Price Slider (Original Unchanged) */}
      <div className="price-filter">
        <div className="price-filter-header">
          <span className="filter-label">Max Budget</span>
          <span className="price-value">₹{maxPrice.toLocaleString()}</span>
        </div>
        <input type="range" className="price-slider" min={2000} max={10000} step={100} value={maxPrice} onChange={(e) => setMaxPrice(Number(e.target.value))} />
        <div className="price-range-labels"><span>₹2,000</span><span>₹10,000</span></div>
      </div>

      {/* --- NEW: 5. Smart Summary Panel --- */}
      <div className="quick-picks">
        <div className="qpick">
          <div className="qpick-label">💰 Cheapest</div>
          <div className="qpick-val">₹{cheapestFlight.price.toLocaleString()}</div>
          <div className="qpick-airline">{cheapestFlight.airline}</div>
        </div>
        <div className="qpick">
          <div className="qpick-label">⚡ Fastest</div>
          <div className="qpick-val">{fastestFlight.duration}</div>
          <div className="qpick-airline">{fastestFlight.airline}</div>
        </div>
        <div className="qpick">
          <div className="qpick-label">⭐ Best Rated</div>
          <div className="qpick-val">{bestRatedFlight.rating} / 5</div>
          <div className="qpick-airline">{bestRatedFlight.airline}</div>
        </div>
      </div>

      {/* Results Header */}
      <div className="results-header">
        {/* --- NEW: 7. Filter Insights --- */}
        <span className="results-count">
          Showing <strong>{filtered.length} flights</strong> under ₹{maxPrice.toLocaleString()}
        </span>
        {(searchFrom || searchTo) && (
          <button className="clear-btn" onClick={() => { setSearchFrom(""); setSearchTo(""); }}>Clear search</button>
        )}
      </div>

      {/* Results List */}
      <div className="flight-cards-list">
        {isLoading ? (
          <div className="empty-state">
            <div className="skeleton-card" /><div className="skeleton-card" /><div className="skeleton-card" />
          </div>
        ) : filtered.length > 0 ? (
          filtered.map((flight, i) => (
            <FlightCard
              key={flight.id}
              flight={flight}
              onSelectFlight={onSelectFlight}
              isSelected={selectedFlight?.id === flight.id}
              isCheapest={flight.price === cheapestFlight.price}
              isFastest={flight.id === fastestFlight.id}
              // --- NEW PROPS ---
              isRecommended={flight.id === recommendedId}
              onCompare={() => handleCompare(flight)}
              isComparing={!!compareList.find(f => f.id === flight.id)}
              index={i}
            />
          ))
        ) : (
          <div className="empty-state">
             {/* ... Original Empty State ... */}
             <div className="empty-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
            </div>
            <div className="empty-title">No flights found</div>
            <div className="empty-sub">Try adjusting your search or filters</div>
            <button className="empty-reset" onClick={() => { setSearchFrom(""); setSearchTo(""); setFilterAirline("All"); setMaxPrice(10000); }}>Reset all filters</button>
          </div>
        )}
      </div>

      {/* --- NEW: 3. Compare Popup Overlay --- */}
      {compareList.length > 0 && (
        <div className="compare-overlay">
          <div className="compare-card">
            <h3>⚖ Flight Comparison ({compareList.length}/2)</h3>
            <div className="compare-grid">
              {compareList.map(f => (
                <div key={f.id} className={`compare-item ${f.id === betterFlight ? "best" : ""}`}>
  <div className="compare-header">
    <strong>{f.airline}</strong>
    {f.id === betterFlight && <span className="winner-badge">🏆 Best</span>}
  </div>

  <div className="compare-row">
    <span>Price</span>
    <span>₹{f.price}</span>
  </div>

  <div className="compare-row">
    <span>Duration</span>
    <span>{f.duration}</span>
  </div>

  <div className="compare-row">
    <span>Rating</span>
    <span>⭐ {f.rating}</span>
  </div>
</div>
              ))}
              {compareList.length < 2 && <div className="compare-placeholder">Select one more flight...</div>}
            </div>
            {compareList.length === 2 && (
               <div className="compare-analysis">
                 Note: {compareList[0].price < compareList[1].price ? compareList[0].airline : compareList[1].airline} is more budget friendly.
               </div>
            )}
            <button className="clear-btn" onClick={() => setCompareList([])}>Close Comparison</button>
          </div>
        </div>
      )}
    </section>
  );
}

export default FlightList;